'use client';

import { useShowErrorState } from '@app/lib/form/hooks/useShowErrorState';
import { isViolationMessage } from '@app/lib/form/isViolationMessage';
import { ViolationError } from '@app/lib/form/contracts/ViolationError';
import { useTranslations } from 'next-intl';
import {
    ChangeEvent,
    ClipboardEventHandler,
    DragEventHandler,
    HTMLInputTypeAttribute,
    KeyboardEvent,
    ReactNode,
    useCallback,
    useMemo,
} from 'react';
import { twJoin, twMerge } from 'tailwind-merge';

interface Props {
    name?: string;
    className?: string;
    inputClassName?: string;
    labelSpanClassName?: string;
    label?: string;
    placeholder?: string;
    type?: HTMLInputTypeAttribute;
    value?: string;
    size?: number;
    labelAddon?: ReactNode;
    leftAddon?: ReactNode;
    rightAddon?: ReactNode;
    prefix?: ReactNode;
    prefixClassName?: string;
    suffix?: ReactNode;
    suffixClassName?: string;
    error?: ViolationError;
    successMessage?: string;
    onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
    defaultValue?: string;
    form?: string;
    description?: ReactNode;
    noSpaces?: boolean;
    noSymbols?: boolean;
    noNumbers?: boolean;
    readonly?: boolean;
    min?: number | string;
    max?: number | string;
    enableErrorOutline?: boolean;
    onPaste?: ClipboardEventHandler<HTMLInputElement>;
    onDrop?: DragEventHandler<HTMLInputElement>;
    autoComplete?: string;
    step?: number;
    maxLength?: number;
    inputMode?: 'email' | 'search' | 'tel' | 'text' | 'url' | 'none' | 'numeric' | 'decimal' | undefined;
    allowNegative?: boolean;
}

export function Input({
    name,
    className,
    inputClassName,
    labelSpanClassName,
    label,
    placeholder,
    type = 'text',
    value,
    size = 1,
    labelAddon,
    leftAddon,
    rightAddon,
    prefix,
    prefixClassName,
    suffix,
    suffixClassName,
    error,
    successMessage,
    onChange,
    defaultValue,
    form,
    description,
    noSpaces = false,
    noSymbols = false,
    noNumbers = false,
    readonly = false,
    min,
    max,
    maxLength,
    enableErrorOutline = false,
    onPaste,
    onDrop,
    autoComplete,
    step,
    inputMode,
    allowNegative = false,
}: Props) {
    const t = useTranslations();

    const [showError, handleChange] = useShowErrorState(error, onChange);

    const displayError = useMemo(() => showError && isViolationMessage(error), [error, showError]);

    const onInput = useCallback(
        (e: ChangeEvent<HTMLInputElement>) => {
            let nextValue = e.target.value;

            if (noSpaces) {
                nextValue = nextValue.replace(/\s/g, '');
            }

            if (noSymbols) {
                nextValue = nextValue.replace(/[^\w\s]/g, '');
            }

            if (noNumbers) {
                nextValue = nextValue.replace(/\d/g, '');
            }

            if (type === 'number' && min !== undefined && Number(min) >= 0) {
                if (nextValue.includes('-') || (nextValue !== '' && Number(nextValue) < Number(min))) {
                    nextValue = nextValue.replace('-', '');
                    if (nextValue !== '' && Number(nextValue) < Number(min)) {
                        nextValue = String(min);
                    }
                }
            }

            if (type === 'text' && (inputMode === 'decimal' || inputMode === 'numeric')) {
                const regex = allowNegative ? /[^-0-9.,]/g : /[^0-9.,]/g;
                nextValue = nextValue.replace(regex, '');

                if (allowNegative && nextValue.includes('-')) {
                    const isNegative = nextValue.startsWith('-');
                    nextValue = (isNegative ? '-' : '') + nextValue.replace(/-/g, '');
                }

                const parts = nextValue.split(/[.,]/);
                if (parts.length > 2) {
                    nextValue = parts[0] + (nextValue.includes('.') ? '.' : ',') + parts.slice(1).join('');
                }
            }

            if (maxLength !== undefined && nextValue.length > maxLength) {
                nextValue = nextValue.slice(0, maxLength);
            }

            e.target.value = nextValue;
            handleChange(e);
        },
        [noSpaces, noSymbols, noNumbers, type, min, inputMode, maxLength, handleChange, allowNegative],
    );

    const onKeyDown = useCallback(
        (e: KeyboardEvent<HTMLInputElement>) => {
            if (type === 'number' && min !== undefined && Number(min) >= 0) {
                if (e.key === '-' || e.key === 'e' || e.key === 'E' || e.key === '+') {
                    e.preventDefault();
                }
            }
        },
        [type, min],
    );

    return (
        <div className={twMerge('flex flex-col gap-0.5', className)}>
            {label != null && (
                <label className="mb-1.5 flex items-start gap-1 text-sm">
                    <span className={twMerge('flex-grow', labelSpanClassName)}>{label}</span>
                    {labelAddon != null && <span className="flex-shrink-0">{labelAddon}</span>}
                </label>
            )}
            <div className="flex items-stretch">
                {leftAddon != null && leftAddon}

                <div
                    className={twJoin(
                        'flex grow border-y border-neutral-400 bg-white',
                        'has-[:focus]:border-neutral-500',
                        displayError && enableErrorOutline && 'border-red-500',
                        leftAddon == null && 'rounded-l-md border-l',
                        rightAddon == null && 'rounded-r-md border-r',
                    )}>
                    {prefix != null && (
                        <div
                            className={twMerge(
                                'flex flex-shrink-0 flex-col items-center justify-center px-3',
                                prefixClassName,
                            )}>
                            {prefix}
                        </div>
                    )}

                    <input
                        className={twMerge(
                            'placeholder:text-black/25 min-w-0 flex-grow bg-transparent py-2 outline-none',
                            prefix == null && 'pl-3',
                            suffix == null && 'pr-3',
                            inputClassName,
                        )}
                        name={name}
                        type={type}
                        value={value}
                        placeholder={type === 'date' && !placeholder ? 'yyyy-mm-dd' : placeholder}
                        onChange={onInput}
                        onKeyDown={onKeyDown}
                        defaultValue={defaultValue}
                        step={step}
                        form={form}
                        size={size}
                        aria-label={label}
                        min={min}
                        readOnly={readonly}
                        max={max}
                        maxLength={maxLength}
                        onPaste={onPaste}
                        onDrop={onDrop}
                        autoComplete={autoComplete}
                        inputMode={inputMode}
                        {...(type === 'date' && { 'data-format': 'yyyy-MM-dd' })}
                    />

                    {suffix != null && (
                        <div
                            className={twMerge(
                                'flex flex-shrink-0 flex-col items-center justify-center px-3',
                                suffixClassName,
                            )}>
                            {suffix}
                        </div>
                    )}
                </div>

                {rightAddon != null && rightAddon}
            </div>

            {description != null && <div className="text-sm text-neutral-500">{description}</div>}
            {successMessage && <div className="text-sm text-green-600">{successMessage}</div>}
            {showError && isViolationMessage(error) && <div className="text-sm text-red-500">{t(error.message)}</div>}
        </div>
    );
}
